import React, { useMemo, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { Box, Skeleton, Typography, alpha, useTheme } from "@mui/material";

// Distinct colours that read well on the navy card (and on the light one)
const PALETTE = {
  dark: ["#FFD27A", "#5fb4ff", "#19E6C1", "#ff8a65", "#b39dff", "#f48fb1"],
  light: ["#c99a2e", "#1f6fc4", "#0f9e85", "#d9623d", "#7b5fd6", "#c2457a"],
};

const pct = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

/**
 * Donut + legend card body (Dashboard "Users by Role", "Units by Type").
 * data:  [{ id, label, value }]
 * icons: optional { [label]: IconComponent } shown in the legend
 */
const BreakdownChart = ({ title, icon, unit = "", data, total, icons = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;
  const [hovered, setHovered] = useState(null);

  const items = useMemo(() => {
    const colors = PALETTE[isDark ? "dark" : "light"];
    return (data || []).map((item, i) => ({
      id: item.label,
      label: item.label,
      value: Number(item.value) || 0,
      color: colors[i % colors.length],
    }));
  }, [data, isDark]);

  const sum = items.reduce((s, i) => s + i.value, 0);
  const chartTotal = total ?? sum;
  const active = items.find((i) => i.id === hovered);

  const header = (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        {icon && (
          <Box
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              color: accent,
              backgroundColor: alpha(accent, isDark ? 0.12 : 0.18),
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="h6" noWrap sx={{ fontWeight: 600, color: theme.palette.secondary[100] }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: muted, whiteSpace: "nowrap" }}>
        {chartTotal} total
      </Typography>
    </Box>
  );

  if (!data) {
    return (
      <Box display="flex" flexDirection="column" height="100%" gap={2}>
        {header}
        <Skeleton variant="circular" width={150} height={150} sx={{ mx: "auto" }} />
        <Skeleton variant="rounded" height={90} />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={1.5} minHeight={0}>
      {header}

      {/* Donut with the total (or the hovered slice) in the middle */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 150 }}>
        {sum > 0 ? (
          <ResponsivePie
            data={items}
            colors={{ datum: "data.color" }}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            innerRadius={0.7}
            padAngle={1.5}
            cornerRadius={3}
            activeOuterRadiusOffset={5}
            borderWidth={0}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            onMouseEnter={(d) => setHovered(d.id)}
            onMouseLeave={() => setHovered(null)}
            tooltip={() => null}
            animate
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 8,
              m: "auto",
              aspectRatio: "1",
              maxHeight: "100%",
              borderRadius: "50%",
              border: `14px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            }}
          />
        )}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.1, color: active ? active.color : theme.palette.secondary[200] }}>
            {active ? active.value : chartTotal}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: muted }}>
            {active ? `${active.label} · ${pct(active.value, chartTotal)}%` : unit || "total"}
          </Typography>
        </Box>
      </Box>

      {/* Legend: colour, name, count, share */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {items.map((item) => {
          const Icon = icons[item.label];
          const share = pct(item.value, chartTotal);
          const on = hovered === item.id;
          return (
            <Box
              key={item.id}
              onMouseEnter={() => setHovered(item.id)}
              onMouseLeave={() => setHovered(null)}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto 36px",
                alignItems: "center",
                columnGap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                cursor: "default",
                backgroundColor: on ? alpha(item.color, 0.12) : "transparent",
                transition: "background-color .15s ease",
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: item.color }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                {Icon && <Icon sx={{ fontSize: 16, color: muted, flexShrink: 0 }} />}
                <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: on ? 700 : 500 }}>
                  {item.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: theme.palette.secondary[200] }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: muted, textAlign: "right" }}>
                {share}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BreakdownChart;
